import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

export const Footer = () => {
  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Horizontal Divider */}
      <div className="border-t border-slate-700"></div>
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {/* Company Info */}
          <div className="flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_industrial-epc-works/artifacts/7eu7rhsm_Calspark%20JPG.jpg"
                alt="Calspark"
                className="h-14 w-14 object-contain" />
              <h3 className="text-white font-bold text-lg">Calspark Electrical & Engineers</h3>
            </div>
            <p className="text-sm leading-relaxed mb-4 text-slate-400">
              Specialized in Engineering, Procurement & Construction (EPC), delivering comprehensive power and electrical infrastructure solutions across Pan India.
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col">
            <h4 className="text-white font-bold text-base mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" onClick={handleLinkClick} className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Home</Link>
              </li>
              <li>
                <Link to="/about" onClick={handleLinkClick} className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">About Us</Link>
              </li>
              <li>
                <Link to="/services" onClick={handleLinkClick} className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Our Services</Link>
              </li>
              <li>
                <Link to="/projects" onClick={handleLinkClick} className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Projects</Link>
              </li>
              <li>
                <Link to="/careers" onClick={handleLinkClick} className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Careers</Link>
              </li>
              <li>
                <Link to="/contact" onClick={handleLinkClick} className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Contact Us</Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="flex flex-col">
            <h4 className="text-white font-bold text-base mb-4">Our Services</h4>
            <ul className="space-y-2">
              <li><Link to="/services#epc" className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">EPC Contracts</Link></li>
              <li><Link to="/services#renewable" className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Renewable Energy</Link></li>
              <li><Link to="/services#substation" className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Substation Works</Link></li>
              <li><Link to="/services#electrical-instrumentation" className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Electrical & Instrumentation</Link></li>
              <li><Link to="/services#transmission" className="text-sm text-slate-400 hover:text-teal-400 hover:pl-1 transition-all duration-200">Transmission Lines</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col">
            <h4 className="text-white font-bold text-base mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-400">Bangalore South, Karnataka, India</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-400">
                  <a href="tel:8147451515" className="hover:text-teal-400 transition-colors block">8147451515</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                <a href="mailto:info@calsparkengineers.com" className="text-sm text-slate-400 hover:text-teal-400 transition-colors">info@calsparkengineers.com</a>
              </li>
              {/* Social Media Icons */}
              <li className="flex gap-4 items-center pt-2">
                {/* LinkedIn */}
                <a href="https://www.linkedin.com/company/calspark-electrical-and-engineers/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-400 hover:scale-110 transition-all duration-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
                {/* Facebook */}
                <a href="https://www.facebook.com/CalsparkElectricalEngineers/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-400 hover:scale-110 transition-all duration-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                {/* YouTube - no link */}
                <span className="text-slate-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </span>
                {/* X (formerly Twitter) - no link */}
                <span className="text-slate-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-10 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">© 2026 Calspark Electrical & Engineers. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" onClick={handleLinkClick} className="text-sm text-slate-500 hover:text-teal-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms-and-conditions" onClick={handleLinkClick} className="text-sm text-slate-500 hover:text-teal-400 transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
